
// Added React import to fix 'Cannot find namespace React' errors for Dispatch and SetStateAction
import React, { useState, useRef, useEffect } from 'react';
import { WorkItem, ReferenceAsset, AppSettings, GeneratedImage, ImageResolution, AspectRatio } from '../types';
import { generateImage } from '../services/geminiService';
import { compositeReferenceImages } from '../utils/helpers';

export function useGeneration(
  workItems: WorkItem[],
  setWorkItems: React.Dispatch<React.SetStateAction<WorkItem[]>>,
  assets: ReferenceAsset[],
  settings: AppSettings,
  resolution: ImageResolution,
  aspectRatio: AspectRatio,
  batchSize: number,
  setIsSettingsOpen: (v: boolean) => void
) {
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Ref for safe async access to current workItems during long-running batch processes
  const workItemsRef = useRef(workItems);
  useEffect(() => {
    workItemsRef.current = workItems;
  }, [workItems]);

  const generateForItem = async (itemId: string, forceRegen = false): Promise<boolean> => {
    // FIX: Only check for platform API key if the provider is Google
    if (settings.apiProvider === 'google' && settings.imageModel === 'gemini-3-pro-image-preview') {
      const hasKey = await (window as any).aistudio?.hasSelectedApiKey();
      if (!hasKey) {
        alert("使用 Gemini 3 Pro 模型需要选择有效的 API Key。");
        await (window as any).aistudio?.openSelectKey();
      }
    }

    // Use the Ref to get the absolute latest state of items
    const currentItems = workItemsRef.current;
    const itemIndex = currentItems.findIndex(i => i.id === itemId);
    if (itemIndex === -1) return false;
    const item = currentItems[itemIndex];

    const updateStatus = (status: WorkItem['status'], errorMessage?: string) => {
      setWorkItems(prev => prev.map(it => it.id === itemId ? { ...it, status, errorMessage: errorMessage || it.errorMessage } : it));
    };
    
    // Reset error when starting new generation
    setWorkItems(prev => prev.map(it => it.id === itemId ? { ...it, status: 'generating', errorMessage: undefined } : it));

    try {
        const selectedAssets = assets.filter(a => item.selectedReferenceIds.includes(a.id));
        let finalReferences: ReferenceAsset[] = [];
        let promptPrefix = "";

        if (selectedAssets.length > 0) {
            const { base64, description } = await compositeReferenceImages(selectedAssets);
            
            // Store the generated description to the item for UI display
            const displayDesc = `Composite Context: ${description}`;
            setWorkItems(prev => prev.map(it => it.id === itemId ? { ...it, referenceDescription: displayDesc } : it));

            finalReferences = [{ id: 'comp', name: 'Ref', keywords: '', type: 'scene', url: '', base64, mimeType: 'image/png' }];
            promptPrefix = `The following is a composite reference image containing multiple elements. ${description}. Please use these visual references to guide the generation.\n\n`;
        } else {
             // Clear description if no assets are selected anymore
             setWorkItems(prev => prev.map(it => it.id === itemId ? { ...it, referenceDescription: undefined } : it));
        }
        
        const finalPrompt = promptPrefix + item.prompt;
        const newImages: GeneratedImage[] = [];
        const batchId = Date.now();
        let fatal = false;
        let lastError = "";

        for (let i = 0; i < batchSize; i++) {
           try {
             if (i > 0) await new Promise(r => setTimeout(r, 1000));
             const res = await generateImage(finalPrompt, finalReferences, resolution, aspectRatio, settings);
             newImages.push({
                 id: Math.random().toString(), url: `data:${res.mimeType};base64,${res.base64}`,
                 base64Data: res.base64, timestamp: Date.now(), batchId, promptUsed: finalPrompt
             });
           } catch (err: any) {
               console.error(err);
               lastError = err.message || "未知错误";
               
               // Only prompt for Google key if the error is specifically about missing entity on Google's side
               if (settings.apiProvider === 'google' && err.message?.includes("Requested entity was not found")) {
                 await (window as any).aistudio?.openSelectKey();
                 fatal = true;
                 break;
               }
               if (err.message?.includes('Quota') || err.message?.includes('balance')) { fatal = true; break; }
               
               // For prohibited content or timeout, we might want to stop the batch for this item immediately
               if (err.message === "PROHIBITED_CONTENT" || err.message === "TIMEOUT") {
                   break;
               }
           }
        }

        if (newImages.length > 0) {
            setWorkItems(prev => prev.map(it => it.id === itemId ? { 
                ...it, status: 'completed', generatedImages: [...newImages, ...(forceRegen ? [] : it.generatedImages)] 
            } : it));
        } else {
            updateStatus('failed', lastError);
            if (fatal) return true;
        }
    } catch (e: any) {
        console.error(e);
        updateStatus('failed', e.message);
    }
    return false;
  };

  const runBatchGeneration = async () => {
    const queueIds = workItemsRef.current
        .filter(i => i.status === 'idle' || i.status === 'failed')
        .map(i => i.id);

    if (queueIds.length === 0) return;

    setIsProcessing(true);
    
    const concurrency = Math.max(1, settings.concurrency || 20);
    let isStopped = false;

    const worker = async () => {
        while (queueIds.length > 0 && !isStopped) {
            const id = queueIds.shift();
            if (id) {
                const isFatal = await generateForItem(id);
                if (isFatal) {
                    isStopped = true;
                    queueIds.length = 0; 
                    alert("批量生成已自动停止：请检查 API 配置或余额。");
                }
            }
        }
    };

    const activeWorkers = Array.from({ length: Math.min(concurrency, queueIds.length) }, () => worker());
    await Promise.all(activeWorkers);
    setIsProcessing(false);
  };

  return { isProcessing, generateForItem, runBatchGeneration };
}
